import api from './index';

/**
 * The four states the header indicator can be in. `signedOut` is kept distinct from
 * `offline` because an expired token means the backend is perfectly healthy — only
 * the session died — and telling users "the CMS is down" in that case is misleading.
 */
export const HEALTH_STATES = {
  HEALTHY: 'healthy',
  DEGRADED: 'degraded',
  SIGNED_OUT: 'signedOut',
  OFFLINE: 'offline',
};

/**
 * Bounds how long a hung backend can hold the indicator in its previous state.
 */
export const HEALTH_TIMEOUT_MS = 5000;

const describeTransportFailure = (error) => {
  if (error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT') {
    return `The backend did not respond within ${HEALTH_TIMEOUT_MS / 1000} seconds.`;
  }

  const status = error?.response?.status;
  if (status) {
    return `The backend responded with an error (HTTP ${status}).`;
  }

  return 'The backend could not be reached. It may be restarting or unavailable.';
};

/**
 * Polls GET /v1/health on the shared axios instance, so the Okta bearer token that
 * ProtectedRoute installs is sent automatically.
 *
 * Never rejects: every outcome, success or failure, is normalized into the same shape
 * so the caller only ever branches on `state`.
 *
 * @returns {Promise<{state: string, db: string|null, version: string|null,
 *                    latencyMs: number, checkedAt: Date, reason: string|null}>}
 */
export const fetchHealth = async () => {
  const startedAt = performance.now();

  try {
    const response = await api.get('health', { timeout: HEALTH_TIMEOUT_MS });
    const { status, db, version } = response?.data ?? {};
    const isHealthy = status === 'UP' && db !== 'DOWN';

    return {
      state: isHealthy ? HEALTH_STATES.HEALTHY : HEALTH_STATES.DEGRADED,
      db: db ?? null,
      version: version ?? null,
      latencyMs: Math.round(performance.now() - startedAt),
      checkedAt: new Date(),
      reason: isHealthy ? null : 'The API is reachable but its database is not responding.',
    };
  } catch (error) {
    const status = error?.response?.status;
    const isAuthFailure = status === 401 || status === 403;

    return {
      state: isAuthFailure ? HEALTH_STATES.SIGNED_OUT : HEALTH_STATES.OFFLINE,
      db: null,
      version: null,
      latencyMs: Math.round(performance.now() - startedAt),
      checkedAt: new Date(),
      reason: isAuthFailure
        ? 'Your session has expired. Sign in again to keep working.'
        : describeTransportFailure(error),
    };
  }
};
